/**
 * PhaseConversionStaircase.tsx — Section 3
 * The active phases stacked in a straight column, each showing its share of
 * new Members, with the transition between phases labelled honestly:
 *
 *   dependent hop   → "↘ N% of <prev> survived — N lost"  (hop survival)
 *   independent hop → "→ independent · N% of new Members reach this phase"
 *
 * Phases flagged `independent` read from events a Member can reach without
 * completing the prior phase (e.g. trials auto-start at signup), so a survival
 * number across those hops would be fiction — we render reach instead, and
 * NEVER a fabricated descending figure.
 *
 * Numbers policy: no clamping. If the data is non-monotonic where it should
 * descend, a data-quality chip says so — Math.min/Math.max previously hid
 * exactly what the operator needed to see (audit, Work Item D).
 *
 * Data: live phase_flow + funnel_top. "Phase N" renders display_index; the
 * internal id `p` is stable (Phase 3 is hidden while the free grant is one
 * Chapter — hidden, not deleted).
 */

import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import SectionHeader from './SectionHeader';
import { FONT, FONT_MONO, PHASE_COLORS, STATUS_CHIP, SURFACE } from './funnelBands';
import { PhaseFlow } from './activationTypes';
import { safeMetric } from '../../utils/safeMetric';

interface Props {
  phaseFlow: PhaseFlow[];
  funnelTop: number;
  loading: boolean;
  onOpenLedger?: () => void;
}

interface Row {
  phase: PhaseFlow;
  pctOfInstalls: number | null;
  hopSurvival: number | null; // dependent hops only — % of prior phase entry
  hopLost: number | null;
  /** entry > prev entry on a dependent hop — impossible for a true funnel. */
  dataQuality: boolean;
  status: keyof typeof STATUS_CHIP;
}

// One health threshold for hop survival, shared by the chip and the hop-line
// colour. (The old code used 60 for the chip and 50 for the line — two answers
// to "is this hop healthy?" on the same row.)
const HOP_HEALTHY_PCT = 60;

const PhaseConversionStaircase: React.FC<Props> = ({ phaseFlow, funnelTop, loading, onOpenLedger }) => {
  const rows: Row[] = phaseFlow.map((phase, idx) => {
    const prev = idx > 0 ? phaseFlow[idx - 1] : null;
    const pctOfInstalls = funnelTop > 0 && phase.entry !== null ? (phase.entry / funnelTop) * 100 : null;
    let hopSurvival: number | null = null;
    let hopLost: number | null = null;
    let dataQuality = false;
    if (prev && !phase.independent && prev.entry !== null && prev.entry > 0 && phase.entry !== null) {
      hopSurvival = (phase.entry / prev.entry) * 100; // unclamped on purpose
      hopLost = prev.entry - phase.entry;
      if (hopSurvival > 100) {
        dataQuality = true; // surfaced, not swallowed
        hopLost = null;
      }
    }
    return { phase, pctOfInstalls, hopSurvival, hopLost, dataQuality, status: 'healthy' };
  });

  // Leakiest dependent hop = largest absolute loss (ties: lowest survival).
  let leakiestIdx = -1;
  rows.forEach((r, idx) => {
    if (r.hopLost === null || r.hopLost <= 0) return;
    if (
      leakiestIdx === -1 ||
      r.hopLost > (rows[leakiestIdx].hopLost ?? 0) ||
      (r.hopLost === rows[leakiestIdx].hopLost &&
        (r.hopSurvival ?? 100) < (rows[leakiestIdx].hopSurvival ?? 100))
    ) {
      leakiestIdx = idx;
    }
  });

  rows.forEach((r, idx) => {
    if (r.dataQuality) {
      // A >100% hop is a data-quality fact — it must never wear the green
      // HEALTHY chip its (survival >= threshold) arithmetic would earn it.
      r.status = 'data check';
    } else if (r.phase.independent) {
      r.status = r.phase.is_retention ? 'retention' : 'watch';
    } else if (idx === leakiestIdx) {
      r.status = 'leakiest hop';
    } else if (idx === 0 || (r.hopSurvival !== null && r.hopSurvival >= HOP_HEALTHY_PCT)) {
      // The first phase has no inbound hop to judge — it is not a leak.
      r.status = 'healthy';
    } else {
      r.status = 'watch';
    }
  });

  const cliff = leakiestIdx > 0 ? {
    from: rows[leakiestIdx - 1].phase.name,
    to: rows[leakiestIdx].phase.name,
    survival: rows[leakiestIdx].hopSurvival,
    lost: rows[leakiestIdx].hopLost,
  } : null;

  // Largest fall-off across ANY consecutive displayed phases (including
  // independent transitions, where it is a reach gap, not a survival loss).
  // A plain for-loop keeps `fallOff` narrowable — the old forEach version
  // needed `as`-casts that disabled type checking for the whole callout.
  let fallOff: { from: string; to: string; gap: number; independent: boolean } | null = null;
  for (let idx = 1; idx < rows.length; idx++) {
    const prevEntry = rows[idx - 1].phase.entry;
    const entry = rows[idx].phase.entry;
    if (prevEntry == null || entry == null) continue;
    const gap = prevEntry - entry;
    if (gap > 0 && (!fallOff || gap > fallOff.gap)) {
      fallOff = {
        from: rows[idx - 1].phase.name,
        to: rows[idx].phase.name,
        gap,
        independent: Boolean(rows[idx].phase.independent),
      };
    }
  }
  const showFallOff = fallOff !== null && (!cliff || fallOff.gap > (cliff.lost ?? 0));

  return (
    <Box>
      <SectionHeader
        num={3}
        title="Phase-to-Phase Conversion"
        description="Each phase's share of this period's new Members, with every transition labelled for what it is: a survival rate where the phases nest, a reach rate where they don't."
      />

      {loading ? (
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: '11px' }} />
      ) : (
        <Box>
          {rows.map((r, idx) => {
            const color = PHASE_COLORS[r.phase.p] || '#1A5F7A';
            const chip = STATUS_CHIP[r.status];
            return (
              <Box key={r.phase.p}>
                {/* Hop line (between phases) */}
                {idx > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, pl: '5px' }}>
                    {r.phase.independent ? (
                      <Typography sx={{ fontFamily: FONT_MONO, fontSize: '10.5px', color: SURFACE.slate }}>
                        → <b>
                          {r.phase.is_retention ? 'independent · retention signal' : 'independent'}
                          {r.pctOfInstalls !== null ? ` · ${r.pctOfInstalls.toFixed(1)}% of new Members reach this phase` : ''}
                        </b>
                      </Typography>
                    ) : r.dataQuality ? (
                      <Typography sx={{ fontFamily: FONT_MONO, fontSize: '10.5px', color: '#B45309' }}>
                        ⚠ <b>data check — this phase counts more Members than the one before it ({safeMetric(r.phase.entry?.toLocaleString())} &gt; {safeMetric(rows[idx - 1].phase.entry?.toLocaleString())})</b>
                      </Typography>
                    ) : (
                      <Typography sx={{ fontFamily: FONT_MONO, fontSize: '10.5px', color: r.hopSurvival !== null && r.hopSurvival < HOP_HEALTHY_PCT ? '#7D1935' : '#146152' }}>
                        ↘ <b>{r.hopSurvival !== null ? `${r.hopSurvival.toFixed(0)}% of ${rows[idx - 1].phase.name} survived` : 'survival n/a'}{r.hopLost !== null ? ` — ${r.hopLost.toLocaleString()} lost` : ''}</b>
                      </Typography>
                    )}
                  </Box>
                )}
                {/* Phase row */}
                <Box sx={{
                  bgcolor: SURFACE.paper, border: `1px solid ${SURFACE.line}`,
                  borderLeft: `5px solid ${color}`, borderRadius: '0 11px 11px 0', p: '12px 16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.75,
                }}>
                  <Box sx={{ lineHeight: 1.2 }}>
                    <Typography sx={{ fontFamily: FONT_MONO, fontSize: '9px', letterSpacing: '0.05em', textTransform: 'uppercase', color: SURFACE.slate }}>
                      Phase {r.phase.display_index ?? r.phase.p}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: '13.5px', fontWeight: 600, color: SURFACE.ink }}>
                      {r.phase.name}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: '11px', color: SURFACE.slate, mt: 0.25 }}>
                      {r.phase.entry !== null ? `${r.phase.entry.toLocaleString()} reached` : 'Awaiting telemetry'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Typography sx={{ fontFamily: FONT_MONO, fontSize: '22px', fontWeight: 800, lineHeight: 1, color }}>
                      {safeMetric(r.pctOfInstalls !== null ? `${r.pctOfInstalls.toFixed(1)}%` : null)}
                    </Typography>
                    <Box sx={{ bgcolor: chip.bg, borderRadius: '6px', px: 1, py: 0.4 }}>
                      <Typography sx={{ fontFamily: FONT_MONO, fontSize: '9px', fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', color: chip.fg, whiteSpace: 'nowrap' }}>
                        {r.status}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}

          {/* Cliff callout — the box (and its Open Leak Ledger button, the
              only in-page navigation to Section 4) renders unconditionally;
              only the callout copy is data-dependent. */}
          {(
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.75, flexWrap: 'wrap',
              bgcolor: '#eaf1f4', border: '1px solid #cfe0e8', borderRadius: '11px', p: '13px 17px', mt: 2,
            }}>
              <Box>
                {!cliff && !showFallOff && (
                  <Typography sx={{ fontFamily: FONT, fontSize: '12.5px', color: '#0d4257', lineHeight: 1.5 }}>
                    No dependent drop to call out in this window — the step-level detail lives in the Leak Ledger.
                  </Typography>
                )}
                {cliff && (
                  <Typography sx={{ fontFamily: FONT, fontSize: '13.5px', color: '#0d4257', lineHeight: 1.5 }}>
                    <b style={{ color: '#1A5F7A' }}>The leakiest hop:</b> {cliff.from} → {cliff.to} keeps only{' '}
                    <b style={{ color: '#1A5F7A' }}>{cliff.survival !== null ? `${cliff.survival.toFixed(0)}%` : '—'}</b>
                    {cliff.lost !== null ? ` — ${cliff.lost.toLocaleString()} lost at this transition.` : '.'} Which step lost them?
                  </Typography>
                )}
                {showFallOff && fallOff && (
                  <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: '#0d4257', lineHeight: 1.5, mt: cliff ? 0.5 : 0 }}>
                    <b style={{ color: '#1A5F7A' }}>Biggest fall-off overall:</b> {fallOff.from} → {fallOff.to},{' '}
                    {fallOff.gap.toLocaleString()} Members
                    {fallOff.independent && <i> (independent transition — not a strict drop)</i>}.
                  </Typography>
                )}
              </Box>
              <Box
                component="button"
                onClick={onOpenLedger}
                sx={{
                  fontFamily: FONT, fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
                  bgcolor: '#7D1935', color: '#fff', border: 'none', borderRadius: '8px', px: 1.9, py: 1.1,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                Open Leak Ledger ↓
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PhaseConversionStaircase;
